import { useEffect, useState } from 'react';
import { api } from '../api';
import { ApiError } from '../api/types';
import type { AltarOferta, AltarTroca, Chefe, ChefeEstado, EventoAgenda, EventoAtual, Penta, PentaEstado } from '../api/evento';
import { t } from '../i18n/idioma';
import { GumpWindow, type WindowAnchor } from './GumpWindow';
import { Icone } from './kit';
import { Quadro } from './eventos/Quadro';
import { ChefeMundo, type AbaChefe } from './eventos/ChefeMundo';
import { TIPO_CHEFE, TIPO_PENTA, TIPO_UB, TIPOS_EVENTO } from './eventos/tipos';
import { Pentagrama, type AbaPenta } from './eventos/Pentagrama';
import { UltimateBattle, type AbaUb } from './eventos/UltimateBattle';

interface Props {
  active: boolean;
  anchor: WindowAnchor;
  onClose(): void;
  /** A barra do Chefe do Mundo que veio pelo hub (techspec-chefe-mundo §07). */
  chefeBarra?: ChefeEstado | null;
  /** A ponta desperta do Pentagrama que veio pelo hub (techspec-pentagrama §09). */
  pentaBarra?: PentaEstado | null;
  /** Abrir já no detalhe de um evento (a barra do chefe abre o dele). */
  abrirTipo?: string | null;
}

/**
 * Mais › Eventos (techspec-eventos §08): o quadro do dia (faixa de 24 h, o que está rolando, um cartão por evento, os
 * que vêm aí) e, ao clicar, o detalhe do evento na mesma janela, com voltar. Inscrever e desistir moram aqui para o
 * quadro e o detalhe usarem o mesmo caminho; o servidor confere tudo de novo. Os prêmios chegam pelo correio.
 */
export function EventoGump({ active, anchor, onClose, chefeBarra = null, pentaBarra = null, abrirTipo = null }: Props) {
  const [atual, setAtual] = useState<EventoAtual | null>(null);
  const [agenda, setAgenda] = useState<EventoAgenda | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState<{ tipo: string; aba: AbaUb | AbaChefe | AbaPenta } | null>(null);
  const [chefe, setChefe] = useState<Chefe | null>(null);
  const [penta, setPenta] = useState<Penta | null>(null);
  const [, setTick] = useState(0);

  const recarregar = async () => {
    try {
      setAtual(await api.eventoAtual());
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : t('evento.naoLeu'));
    }
  };

  const recarregarAgenda = async () => {
    try {
      const a = await api.eventoAgenda();
      setAgenda(a);
    } catch {
      /* o quadro mostra só o que o atual já deu */
    }
  };

  const recarregarChefe = async () => {
    try {
      setChefe(await api.eventoChefe());
    } catch {
      /* o detalhe mostra "carregando"; o quadro segue com a agenda */
    }
  };

  const recarregarPenta = async () => {
    try {
      setPenta(await api.eventoPentagrama());
    } catch {
      /* o detalhe mostra "carregando" */
    }
  };

  useEffect(() => {
    if (!active) return;
    setSel(abrirTipo ? { tipo: abrirTipo, aba: 'recompensas' } : null);
    setErro(null);
    void recarregar();
    void recarregarAgenda();
    void recarregarChefe();
    const id = window.setInterval(() => setTick(n => n + 1), 1000);
    return () => window.clearInterval(id);
  }, [active, abrirTipo]);

  // Com o chefe de pé e o detalhe dele aberto, o ranking e o lugar no dano se atualizam a cada 5 s.
  const vendoChefe = active && sel?.tipo === TIPO_CHEFE && !!chefeBarra?.id && chefeBarra.estado === 0;
  useEffect(() => {
    if (!vendoChefe) return;
    const id = window.setInterval(() => void recarregarChefe(), 5000);
    return () => window.clearInterval(id);
  }, [vendoChefe]);

  if (!active) return null;

  const agir = async (fn: () => Promise<unknown>, falha: string, depois: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
      setErro(null);
      await depois();
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : t(falha));
    } finally {
      setBusy(false);
    }
  };
  const inscrever = () => void agir(() => api.eventoInscrever(), 'evento.naoInscreveu', recarregar);
  const desistir = () => void agir(() => api.eventoDesistir(), 'evento.naoDesistiu', recarregar);
  const trocar = async (o: AltarOferta): Promise<AltarTroca | null> => {
    let feito: AltarTroca | null = null;
    await agir(async () => { feito = await api.eventoAltarTrocar(o.id, chefe?.versao ?? 0); }, 'chefe.altar.naoTrocou',
      async () => { await Promise.all([recarregarChefe(), recarregarAgenda()]); });
    if (!feito) void recarregarChefe();   // recusou (versão velha, esgotou): relê o Altar
    return feito;
  };

  const moedaId = (atual?.moeda ?? agenda?.moeda) === 'gold' ? 'gold' : 'silver';
  const moeda = t(moedaId === 'gold' ? 'item.gold' : 'item.silver').toLocaleLowerCase();
  const razao = razaoDaTrava(atual?.trava, atual?.inscrito === true);
  const tipo = sel ? TIPOS_EVENTO.find(x => x.id === sel.tipo) ?? null : null;
  const abrir = (id: string) => {
    setErro(null);
    setSel({ tipo: id, aba: 'recompensas' });
    if (id === TIPO_CHEFE) void recarregarChefe();
    if (id === TIPO_PENTA) void recarregarPenta();
  };

  return (
    <GumpWindow
      title={t('evento.mais.titulo')}
      anchor={anchor}
      snapAnchor
      className="win--evento"
      headLead={<Icone nome="evento" tam={18} />}
      onClose={onClose}
    >
      <div className="ev">
        {!tipo || !sel ? (
          <Quadro
            atual={atual}
            agenda={agenda}
            chefe={chefeBarra}
            penta={pentaBarra}
            busy={busy}
            razao={razao}
            moeda={moeda}
            onAbrir={abrir}
            onInscrever={inscrever}
            onDesistir={desistir}
          />
        ) : (
          <>
            <button type="button" className="ev-voltar" onClick={() => { setSel(null); setErro(null); }} aria-label={t('evento.lista.voltarAria')}>
              <span aria-hidden="true">‹</span> {t('evento.mais.titulo')}
            </button>
            {tipo.id === TIPO_UB && (
              <UltimateBattle
                atual={atual}
                agenda={agenda}
                abaInicial={sel.aba as AbaUb}
                busy={busy}
                razao={razao}
                moeda={moeda}
                onInscrever={inscrever}
                onDesistir={desistir}
              />
            )}
            {tipo.id === TIPO_PENTA && (
              <Pentagrama
                penta={penta}
                barra={pentaBarra}
                abaInicial={sel.aba as AbaPenta}
              />
            )}
            {tipo.id === TIPO_CHEFE && (
              <ChefeMundo
                chefe={chefe}
                agenda={agenda}
                barra={chefeBarra}
                abaInicial={sel.aba as AbaChefe}
                busy={busy}
                onTrocar={trocar}
              />
            )}
          </>
        )}
        {erro && <p className="ev-dica ev-dica--aviso" role="alert">{erro}</p>}
      </div>
    </GumpWindow>
  );
}

function razaoDaTrava(trava: string | null | undefined, inscrito: boolean): string | null {
  if (inscrito) return null;
  if (trava === 'evento.morto') return t('evento.trava.morto');
  if (trava === 'evento.cacada') return t('evento.trava.cacada');
  if (trava === 'evento.saldo') return t('evento.trava.saldo');
  if (trava === 'evento.troca') return t('evento.trava.troca');
  if (trava === 'evento.semSessao') return null;
  return trava ? t('evento.trava.fechado') : null;
}
